/**
 * Row model shared by the Schedules table, timeline and health strip.
 *
 * All three need the same next-run prediction, and predicting is a cron walk —
 * so it happens exactly once per schedule here, and the views read the result.
 * In particular `sortKey` is precomputed: calling `nextRunSortKey` from inside
 * a comparator would re-walk the cron O(n log n) times.
 */
import type { PipelineSchedule } from '../services/pipelineOrchestrator';
import { getNextRun, nextRunSortKey, type NextRun } from './cronNextRun';
import { humanizeCron, parseCronForPicker } from './cronCalendar';
import { humanizeSlug } from './pipelineZones';

/**
 * How a schedule's cadence is being described, so the view can style it.
 * `raw` means we refused to humanize and are showing the cron itself.
 */
export interface CadenceLabel {
  text: string;
  kind: 'api' | 'humanized' | 'raw';
}

export interface ScheduleRow {
  schedule: PipelineSchedule;
  /** Next occurrence, or null when the cron or timezone can't be read. */
  next: NextRun | null;
  /** Total, finite ordering key. See `nextRunSortKey`. */
  sortKey: number;
  cadence: CadenceLabel;
  /** Zone slugs grouped to their collectors — one entry per distinct zone. */
  zones: Array<{ zoneSlug: string; label: string; collectors: string[] }>;
}

export type ScheduleStatusFilter = 'all' | 'active' | 'paused';

export interface ScheduleFilters {
  query: string;
  status: ScheduleStatusFilter;
  hidePaused: boolean;
}

export const EMPTY_SCHEDULE_FILTERS: ScheduleFilters = {
  query: '',
  status: 'all',
  hidePaused: false
};

/**
 * A schedule's cadence in words — but only when that can be done honestly.
 *
 * `humanizeCron` destructures `[minute, hour, , , dayOfWeek]` and throws away
 * day-of-month and month entirely, so `0 3 1 * *` (3 AM on the 1st) renders as
 * "Every day at 3:00 AM ET". On a monitoring surface that is not a cosmetic
 * problem; it is a false statement about when a job runs.
 *
 * `parseCronForPicker` rejects a non-wildcard day-of-month or month, and any
 * hour/minute that isn't a bare integer — precisely the set `humanizeCron`
 * cannot represent. Using it as the gate means the two cannot drift apart.
 * Anything it rejects falls back to the raw expression, and the Next run
 * column carries the real answer.
 */
export function cadenceLabel(schedule: PipelineSchedule): CadenceLabel {
  if (schedule.cron_description) {
    return { text: schedule.cron_description, kind: 'api' };
  }
  if (parseCronForPicker(schedule.cron_expression)) {
    return {
      text: humanizeCron(schedule.cron_expression, schedule.time_zone),
      kind: 'humanized'
    };
  }
  return { text: schedule.cron_expression, kind: 'raw' };
}

/**
 * Groups a schedule's `{zone_slug, collector}` pairs into one entry per zone.
 * The wire format repeats the zone once per collector, so a 14-zone schedule
 * with two collectors each arrives as 28 entries.
 */
export function groupScheduleZones(schedule: PipelineSchedule): ScheduleRow['zones'] {
  const grouped = new Map<string, string[]>();
  for (const zone of schedule.pipeline_config.zones ?? []) {
    const existing = grouped.get(zone.zone_slug) ?? [];
    existing.push(zone.collector);
    grouped.set(zone.zone_slug, existing);
  }
  return Array.from(grouped.entries()).map(([zoneSlug, collectors]) => ({
    zoneSlug,
    label: humanizeSlug(zoneSlug),
    collectors
  }));
}

/** Decorates each schedule with its prediction. Memoize on [schedules, now]. */
export function buildScheduleRows(schedules: PipelineSchedule[], now: Date): ScheduleRow[] {
  return schedules.map(schedule => ({
    schedule,
    next: getNextRun(schedule.cron_expression, schedule.time_zone, now),
    sortKey: nextRunSortKey(schedule, now),
    cadence: cadenceLabel(schedule),
    zones: groupScheduleZones(schedule)
  }));
}

export type ScheduleSortKey = 'next' | 'name' | 'lastRun';
export interface ScheduleSort {
  key: ScheduleSortKey;
  dir: 'asc' | 'desc';
}

export const DEFAULT_SCHEDULE_SORT: ScheduleSort = { key: 'next', dir: 'asc' };

/** Rows that can't be scheduled at all — paused or unreadable. */
const PAUSED_KEY = Number.MAX_SAFE_INTEGER - 1;

function compareByName(a: ScheduleRow, b: ScheduleRow): number {
  return a.schedule.schedule_name.localeCompare(b.schedule.schedule_name);
}

function compareByLastRun(a: ScheduleRow, b: ScheduleRow): number {
  const at = a.schedule.last_triggered_at ? Date.parse(a.schedule.last_triggered_at) : -Infinity;
  const bt = b.schedule.last_triggered_at ? Date.parse(b.schedule.last_triggered_at) : -Infinity;
  return at - bt;
}

/**
 * Sorts rows, keeping unschedulable ones pinned to the bottom.
 *
 * `nextRunSortKey` encodes three tiers — a real instant, then paused, then
 * unreadable. Reversing the whole list for a descending sort would float the
 * junk tiers to the top, burying live schedules under rows that will never
 * run. So the schedulable rows are sorted and reversed on their own, and the
 * tail is always appended in the same order: paused first, then broken.
 */
export function sortScheduleRows(rows: ScheduleRow[], sort: ScheduleSort): ScheduleRow[] {
  const direction = sort.dir === 'asc' ? 1 : -1;

  // Sorting by name is about identity, not schedulability, so every row takes
  // part and nothing is pinned.
  if (sort.key === 'name') {
    return [...rows].sort((a, b) => compareByName(a, b) * direction);
  }

  const schedulable: ScheduleRow[] = [];
  const paused: ScheduleRow[] = [];
  const broken: ScheduleRow[] = [];

  for (const row of rows) {
    if (row.sortKey < PAUSED_KEY) schedulable.push(row);
    else if (row.sortKey === PAUSED_KEY) paused.push(row);
    else broken.push(row);
  }

  const compare =
    sort.key === 'lastRun'
      ? compareByLastRun
      : (a: ScheduleRow, b: ScheduleRow) => a.sortKey - b.sortKey;

  schedulable.sort((a, b) => compare(a, b) * direction || compareByName(a, b));
  paused.sort(compareByName);
  broken.sort(compareByName);

  return [...schedulable, ...paused, ...broken];
}

/** Case-insensitive match across the fields an admin would actually type. */
function matchesQuery(row: ScheduleRow, term: string): boolean {
  const t = term.trim().toLowerCase();
  if (!t) return true;
  const s = row.schedule;
  return (
    s.schedule_name.toLowerCase().includes(t) ||
    (s.tenants?.tenant_name ?? '').toLowerCase().includes(t) ||
    (s.description ?? '').toLowerCase().includes(t) ||
    row.zones.some(z => z.zoneSlug.toLowerCase().includes(t) || z.label.toLowerCase().includes(t))
  );
}

/**
 * Applies the toolbar filters.
 *
 * Deliberately client-side: pushing `is_enabled` into the params passed to
 * `usePipelineSchedules` would fork the React Query cache key, costing a
 * network round-trip and a loading flash on every toggle, and would leave the
 * health strip counting a filtered population instead of the real one.
 */
export function filterScheduleRows(rows: ScheduleRow[], filters: ScheduleFilters): ScheduleRow[] {
  return rows.filter(row => {
    const enabled = row.schedule.is_enabled;
    if (filters.status === 'active' && !enabled) return false;
    if (filters.status === 'paused' && enabled) return false;
    if (filters.hidePaused && !enabled) return false;
    return matchesQuery(row, filters.query);
  });
}

export interface ScheduleSummary {
  total: number;
  active: number;
  paused: number;
  /** Enabled schedules whose most recent run did not succeed. */
  failing: number;
  /** Soonest upcoming run among enabled, predictable schedules. */
  nextUp: ScheduleRow | null;
}

/** Counts for the health strip. Pass the *filtered* rows so it agrees with the table. */
export function summarizeSchedules(rows: ScheduleRow[]): ScheduleSummary {
  let active = 0;
  let paused = 0;
  let failing = 0;
  let nextUp: ScheduleRow | null = null;

  for (const row of rows) {
    const { is_enabled: enabled, last_run_status: status } = row.schedule;
    if (enabled) active++;
    else paused++;

    if (enabled && (status === 'failed' || status === 'partial_success')) failing++;

    if (enabled && row.next && (!nextUp || row.sortKey < nextUp.sortKey)) {
      nextUp = row;
    }
  }

  return { total: rows.length, active, paused, failing, nextUp };
}
